/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, Divider, Typography, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ProposalError, githubPr, useProposalsApi, type PendingProposal } from './proposals';

const useStyles = makeStyles(theme => ({
  root: { marginTop: theme.spacing(3) },
  heading: { fontSize: '0.9375rem', fontWeight: 600, marginTop: theme.spacing(2) },
  headingLine: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: theme.spacing(1) },
  note: { color: theme.palette.text.secondary, fontSize: '0.8125rem' },
  // Same grid and card shape as the live servers above; a dashed border marks
  // a proposal that isn't live yet.
  list: {
    listStyle: 'none', padding: 0, margin: theme.spacing(1.5, 0, 0),
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: theme.spacing(2),
  },
  cell: { display: 'flex', minWidth: 0 },
  card: {
    flex: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing(1), minWidth: 0,
    padding: theme.spacing(2), borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.text.disabled}`, backgroundColor: theme.palette.background.paper,
  },
  top: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: theme.spacing(1) },
  name: { fontSize: '1rem', fontWeight: 600, margin: 0 },
  title: { fontSize: '0.8125rem', flex: 1 },
  detail: { color: theme.palette.text.secondary, fontSize: '0.8125rem' },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1), flexWrap: 'wrap', marginTop: 'auto' },
  progress: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: theme.spacing(1.5), rowGap: theme.spacing(0.5), margin: theme.spacing(1, 0, 0.5), minWidth: 0 },
  track: { display: 'inline-flex', alignItems: 'center', flex: '0 0 160px', listStyle: 'none', padding: 0, margin: 0 },
  step: { display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, '&:last-child': { flex: '0 0 10px' }, '&:not(:last-child)::after': { content: '""', height: 2, flex: 1, backgroundColor: theme.palette.divider },
    // Light the segment leaving a finished step, so progress reads at a glance.
    '&[data-state="done"]:not(:last-child)::after': { backgroundColor: theme.palette.primary.main } },
  dot: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', width: 10, height: 10, flex: '0 0 10px', borderRadius: '50%', border: `2px solid ${theme.palette.text.disabled}` },
  done: { backgroundColor: theme.palette.primary.main, borderColor: theme.palette.primary.main },
  current: { borderColor: theme.palette.primary.main, animation: '$pulse 1.6s ease-in-out 3', '@media (prefers-reduced-motion: reduce)': { animation: 'none' } },
  failed: { backgroundColor: theme.palette.error.main, borderColor: theme.palette.error.main, color: theme.palette.error.contrastText },
  hidden: { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 },
  '@keyframes pulse': { '0%, 100%': { transform: 'scale(1)', opacity: 1 }, '50%': { transform: 'scale(1.2)', opacity: 0.65 } },
  viewPr: { whiteSpace: 'nowrap' },
  error: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginTop: theme.spacing(3) },
}));

// The Add/Update chip already says what the PR does; drop the bot's title prefix.
const displayTitle = (title: string) => title.replace(/^\[MCP Catalog\] (?:Add|Update) /, '');

const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

const relativeTime = (createdAt: string): string => {
  const elapsed = Date.now() - new Date(createdAt).getTime();
  if (!Number.isFinite(elapsed)) return '';
  const seconds = Math.round(elapsed / 1000);
  if (seconds < 60) return formatter.format(-seconds, 'second');
  const minutes = Math.round(elapsed / 60_000);
  if (minutes < 60) return formatter.format(-minutes, 'minute');
  const hours = Math.round(elapsed / 3_600_000);
  if (hours < 24) return formatter.format(-hours, 'hour');
  const days = Math.round(elapsed / 86_400_000);
  if (days < 30) return formatter.format(-days, 'day');
  const months = Math.round(elapsed / (30 * 86_400_000));
  if (months < 12) return formatter.format(-months, 'month');
  return formatter.format(-Math.round(elapsed / (365 * 86_400_000)), 'year');
};

type StepState = 'done' | 'current' | 'failed' | 'todo' | 'unknown';

const REVIEW_TEXT = { approved: 'Approved', changes_requested: 'Changes requested', awaiting: 'Awaiting review' } as const;

const checksText = ({ state, passed, total }: NonNullable<PendingProposal['checks']>) => {
  if (state === 'none') return 'No checks';
  if (state === 'running') return `Checks running (${passed}/${total})`;
  if (state !== 'failing') return `Checks ${passed}/${total} passing`;
  return total - passed === 1 ? '1 check failing' : `Checks failing (${passed}/${total} passing)`;
};

const stepSpoken = (label: string, state: StepState) => {
  if (label === 'Checks' && state === 'failed') return 'failing';
  return label === 'Checks' && state === 'current' ? 'running' : state;
};

const reviewProgress = (item: PendingProposal) => {
  const unavailable = item.status !== 'ok' || !item.checks || !item.review;
  if (unavailable) return { ready: false, steps: ['done', 'unknown', 'todo', 'todo'] as StepState[], summary: 'Status unavailable' };
  const checks = item.checks!;
  const review = item.review!;
  const checksDone = checks.state === 'passing' || checks.state === 'none';
  const ready = review === 'approved' && checksDone;
  const checksStep: StepState = checksDone ? 'done' : ({ failing: 'failed', running: 'current' } as const)[checks.state as 'failing' | 'running'];
  const reviewStep: StepState = ({ approved: 'done', changes_requested: 'failed', awaiting: checksDone ? 'current' : 'todo' } as const)[review];
  const reviewText = REVIEW_TEXT[review];
  return { ready, steps: ['done', checksStep, reviewStep, ready ? 'current' : 'todo'] as StepState[],
    summary: `${checksText(checks)} · ${reviewText}${ready ? ' · Awaiting merge' : ''}` };
};

const PendingCard = ({ item }: { item: PendingProposal }) => {
  const classes = useStyles();
  const headingId = React.useId();
  const link = githubPr(item.url);
  const progress = reviewProgress(item);
  return (
    <li className={classes.cell}>
      <article className={classes.card} aria-labelledby={headingId}>
        <Box className={classes.top}>
          <Typography component="h3" id={headingId} className={classes.name}>{item.name}</Typography>
          <Chip size="small" variant="outlined" label={item.mode === 'add' ? 'Add' : 'Update'} />
        </Box>
        <Typography className={classes.title}>{displayTitle(item.title)}</Typography>
        <Box className={classes.progress}>
          <ol className={classes.track} aria-label={`Review progress for ${item.name}`}>
            {(['Opened', 'Checks', 'Approved', 'Merged'] as const).map((label, index) => {
              const state = progress.steps[index];
              const dotState = ({ done: classes.done, current: classes.current, failed: classes.failed } as Partial<Record<StepState, string>>)[state] ?? '';
              return (
                <li key={label} className={classes.step} data-state={state}>
                  <span className={`${classes.dot} ${dotState}`} aria-hidden="true">
                    {state === 'failed' && <CloseIcon style={{ fontSize: 8 }} />}
                  </span>
                  <span className={classes.hidden}>{label}: {stepSpoken(label, state)}</span>
                </li>
              );
            })}
          </ol>
          <Typography className={classes.detail}>{progress.summary}</Typography>
        </Box>
        <Box className={classes.footer}>
          <Typography className={classes.detail}>
            opened by {item.author} ·{' '}
            <time dateTime={item.createdAt} title={new Date(item.createdAt).toLocaleString()}>{relativeTime(item.createdAt)}</time>
          </Typography>
          {link ? (
            <Button
              className={classes.viewPr}
              variant="outlined"
              color="primary"
              size="small"
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View PR #${item.number} for ${item.name} (opens in a new tab)`}
            >
              View PR
            </Button>
          ) : (
            <Typography className={classes.detail}>#{item.number}</Typography>
          )}
        </Box>
      </article>
    </li>
  );
};

// `refresh` changes after a proposal is submitted; the current cards stay up while the list reloads.
export const PendingProposals = ({ refresh = 0 }: { refresh?: number }) => {
  const classes = useStyles();
  const api = useProposalsApi();
  const [items, setItems] = useState<PendingProposal[] | null>(null);
  const [error, setError] = useState<{ message: string; retryable: boolean } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => { setError(null); setAttempt(a => a + 1); }, []);

  useEffect(() => {
    let active = true;
    api.pending().then(result => {
      if (!active) return;
      setItems(result.items);
      setError(null);
    }).catch(e => {
      if (!active) return;
      // Retrying a budget or sign-in refusal only repeats it, so show the server's reason instead.
      const final = e instanceof ProposalError && [401, 403, 429].includes(e.status);
      setError({ message: final && e.body.message ? e.body.message : 'Couldn’t load pending proposals.', retryable: !final });
    });
    return () => { active = false; };
  }, [api, attempt, refresh]);

  if (error) {
    return (
      <Box className={classes.error}>
        <Typography color="textSecondary" variant="body2">{error.message}</Typography>
        {error.retryable && <Button size="small" color="primary" onClick={retry}>Retry</Button>}
      </Box>
    );
  }
  if (!items?.length) return null;

  const readyCount = items.filter(item => reviewProgress(item).ready).length;

  return (
    <Box className={classes.root}>
      <Divider />
      <Box className={classes.headingLine}>
        <Typography component="h2" className={classes.heading}>Pending review</Typography>
        <Typography className={classes.note}>· {items.length} pending{readyCount > 0 ? ` · ${readyCount} ready to merge` : ''}</Typography>
      </Box>
      <Typography className={classes.note}>Not live until the pull request is reviewed and merged.</Typography>
      <ul className={classes.list} aria-label="Pending proposals">
        {items.map(item => <PendingCard key={item.number} item={item} />)}
      </ul>
    </Box>
  );
};
